import React, { useMemo } from "react";
import { Grid, Box, Typography, alpha, Tooltip } from "@mui/material";
import EuroIcon from "@mui/icons-material/Euro";

import InputFieldNumber from "../../../common/inputs/InputFieldNumber";
import { EEGTariff, EEGTariffUpdate } from "../../../../types";
import { fonts, colors } from "../../../../styles/theme";

// Fachliche Tooltip-Texte für die EEG-Tarif-Felder
const TOOLTIPS = {
  zuschlagswert: "Der im Ausschreibungsverfahren verwendete Wert gemäß EEG.",
  referenzertrag:
    "Summe aller Anlagen-Referenzerträge. Wird automatisch aus den Anlagen berechnet.",
  standortguete:
    "Verhältnis von P50-Ertrag zu Referenzertrag. Zeigt die Qualität des Standorts im Vergleich zum Referenzstandort.",
  korrekturfaktor:
    "Aus der Standortgüte interpolierter Faktor gemäß EEG. Korrigiert den Zuschlagswert je nach Standortqualität.",
};

interface EEGTariffSectionProps {
  eegTariff: EEGTariff | null;
  canEdit: boolean;
  onFieldChange: (field: keyof EEGTariffUpdate) => (value: string) => void;
}

/**
 * EEG-Tarif Section
 *
 * Layout:
 * - Zeile 1: Zuschlagswert (editierbar, Pflichtfeld) | Referenzertrag MWh (readonly, berechnet aus Anlagen)
 * - Zeile 2: Standortgüte % (readonly, berechnet) | Korrekturfaktor (readonly, berechnet)
 * - Highlight-Banner: Preis / kWh (prominent, berechnet) – der wichtigste Wert
 *
 * Berechnete Felder werden vom Backend geliefert:
 * - Referenzertrag = Summe aller Anlagen-Referenzerträge
 * - Standortgüte = P50 / Referenzertrag * 100
 * - Korrekturfaktor = aus Stützpunkttabelle interpoliert (gemäß § 36h Abs. 1 EEG 2023)
 * - Preis / kWh = Zuschlagswert × Korrekturfaktor
 */
const EEGTariffSection: React.FC<EEGTariffSectionProps> = ({
  eegTariff,
  canEdit,
  onFieldChange,
}) => {
  // Helper: Wandelt Zahl in String für Anzeige
  const numToStr = (value: number | undefined | null): string => {
    if (value === undefined || value === null) return "";
    return String(value);
  };

  // Helper: Formatiert Zahl mit 2 Nachkommastellen für Anzeige (readonly Felder)
  const numToStr2Decimals = (value: number | undefined | null): string => {
    if (value === undefined || value === null) return "";
    // Runde auf 2 Dezimalstellen
    return (Math.round(value * 100) / 100).toFixed(2);
  };

  // Berechnung: Preis / kWh = Zuschlagswert × Korrekturfaktor
  // Gibt sowohl den gerundeten (2 Dezimalstellen) als auch den exakten Wert (4 Dezimalstellen) zurück
  const { preisProKwh, preisProKwhExakt } = useMemo(() => {
    const zuschlagswert = eegTariff?.zuschlagswert;
    const korrekturfaktor = eegTariff?.korrekturfaktor;

    if (
      zuschlagswert === undefined ||
      zuschlagswert === null ||
      korrekturfaktor === undefined ||
      korrekturfaktor === null
    ) {
      return { preisProKwh: undefined, preisProKwhExakt: undefined };
    }

    const exakt = zuschlagswert * korrekturfaktor;
    return {
      preisProKwh: Math.round(exakt * 100) / 100,
      preisProKwhExakt: exakt,
    };
  }, [eegTariff?.zuschlagswert, eegTariff?.korrekturfaktor]);

  // Formatiert den Preis für die prominente Anzeige (deutsche Notation, 2 Nachkommastellen)
  const formatPreisDisplay = (value: number | undefined): string => {
    if (value === undefined) return "—";
    return value.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Formatiert den exakten Preis für den Tooltip (deutsche Notation, 4 Nachkommastellen)
  const formatPreisExakt = (value: number | undefined): string => {
    if (value === undefined) return "—";
    return value.toLocaleString("de-DE", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
  };

  return (
    <>
      <Grid container spacing={3}>
        {/* Erste Reihe: Zuschlagswert (Pflichtfeld) | Referenzertrag (Pflichtfeld) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <InputFieldNumber
            label="Zuschlagswert"
            value={numToStr(eegTariff?.zuschlagswert)}
            onChange={onFieldChange("zuschlagswert")}
            readOnly={!canEdit}
            suffix="Cent/kWh"
            required
            tooltip={TOOLTIPS.zuschlagswert}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <InputFieldNumber
            label="Referenzertrag"
            value={numToStr(eegTariff?.referenzertrag)}
            readOnly={true}
            suffix="MWh"
            tooltip={TOOLTIPS.referenzertrag}
          />
        </Grid>

        {/* Zweite Reihe: Standortgüte (berechnet) | Korrekturfaktor (berechnet) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <InputFieldNumber
            label="Standortgüte"
            value={numToStr2Decimals(eegTariff?.standortguete)}
            readOnly={true}
            suffix="%"
            tooltip={TOOLTIPS.standortguete}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <InputFieldNumber
            label="Korrekturfaktor"
            value={numToStr2Decimals(eegTariff?.korrekturfaktor)}
            readOnly={true}
            tooltip={TOOLTIPS.korrekturfaktor}
          />
        </Grid>
      </Grid>

      {/* Highlight-Banner für Preis / kWh (unten) */}
      <Tooltip
        title={
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Genauer Wert: {formatPreisExakt(preisProKwhExakt)} Cent/kWh
            </Typography>
            <Typography variant="caption" sx={{ color: colors.tooltip.text }}>
              Der effektive Vergütungssatz pro eingespeister Kilowattstunde nach
              Anwendung des standortabhängigen Korrekturfaktors.
            </Typography>
          </Box>
        }
        arrow
        placement="top"
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${alpha(colors.accent.teal, 0.08)} 0%, ${alpha(colors.accent.teal, 0.02)} 100%)`,
            borderRadius: 2,
            p: 2.5,
            mt: 3,
            borderLeft: `4px solid ${colors.accent.teal}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "help",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: alpha(colors.accent.teal, 0.12),
                color: colors.accent.teal,
              }}
            >
              <EuroIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  fontSize: "0.8rem",
                }}
              >
                Preis / kWh
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.disabled",
                  fontSize: "0.7rem",
                }}
              >
                Zuschlagswert × Korrekturfaktor
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 500,
              color: colors.accent.teal,
              fontFamily: fonts.monospace,
              fontSize: "1.75rem",
              letterSpacing: "-0.02em",
            }}
          >
            {formatPreisDisplay(preisProKwh)}{" "}
            <Typography
              component="span"
              sx={{
                fontSize: "1rem",
                fontWeight: 400,
                color: "text.secondary",
              }}
            >
              Cent/kWh
            </Typography>
          </Typography>
        </Box>
      </Tooltip>
    </>
  );
};

export default React.memo(EEGTariffSection);
